
import ProductCard from "./ProductCard";
import { Rainbow, Smile } from "lucide-react";
import confetti from "canvas-confetti";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useState } from "react";

const products = [
  {
    id: "1",
    name: "Hout en Parel Armband",
    price: 3.50,
    image: "/lovable-uploads/afe85108-72bb-46ab-8d23-e47c1361ac05.png",
    category: "armbandjes",
    stock: 5,
  },
  {
    id: "2",
    name: "Groen gestreepte ring",
    price: 2.00,
    image: "/lovable-uploads/432539b9-c41c-4839-98bc-1e4c901bf214.png",
    category: "ringen",
    stock: 4,
  },
  {
    id: "3",
    name: "Bubbelthee Oorbellen",
    price: 4.50,
    image: "/lovable-uploads/9ee4e88c-d9eb-441a-b8ae-8d247bb3283d.png",
    category: "oorbellen",
    stock: 2,
  },
  {
    id: "4",
    name: "Lollypop Dream Oorbellen",
    price: 5,
    image: "/lovable-uploads/27387ea5-a8ec-4735-a1e6-9663793c6166.png",
    category: "oorbellen",
  },
  {
    id: "6",
    name: "Ocean Waves Armband",
    price: 13.99,
    image: "/lovable-uploads/816c5780-90e6-435c-9c23-3f54fb9147ba.png",
    category: "armbandjes",
  }
];

const categories = [
  "Alles",
  "Armbandjes",
  "Ringen",
  "Kettingen",
  "Telefoonkoorden",
  "Oorbellen",
];

const ProductGrid = () => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(["Alles"]);

  const handleLogoClick = () => {
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
    
    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const particleCount = 50;
    confetti({
      ...defaults,
      particleCount,
      origin: { x: randomInRange(0.3, 0.7), y: randomInRange(0.3, 0.5) },
      colors: ['#4D9DE0', '#FF6B9C', '#95F985'].map(color => color + '80')
    });
  };

  const handleCategoryChange = (categories: string[]) => {
    if (categories.includes("Alles") && !selectedCategories.includes("Alles")) {
      setSelectedCategories(["Alles"]);
      return;
    }
    
    if (selectedCategories.includes("Alles") && categories.length > 1) {
      setSelectedCategories(categories.filter(cat => cat !== "Alles"));
      return;
    }

    if (categories.length === 0) {
      setSelectedCategories(["Alles"]);
      return;
    }

    setSelectedCategories(categories);
  };

  const filteredProducts = selectedCategories.includes("Alles") || selectedCategories.length === 0
    ? products
    : products.filter(product => 
        selectedCategories.map(cat => cat.toLowerCase()).includes(product.category)
      );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mb-12 flex flex-col items-center justify-center space-y-4">
        <div className="bg-white/90 backdrop-blur-sm px-8 py-4 rounded-lg shadow-lg">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Rainbow 
                size={48} 
                className="animate-float" 
                strokeWidth={2.5}
                style={{
                  stroke: "url(#rainbow-gradient)"
                }}
              />
            </div>
            <div 
              className="flex flex-col items-start cursor-pointer relative" 
              onClick={handleLogoClick}
            >
              <div className="flex items-center gap-1">
                <span className="inline-block transform transition-transform hover:scale-110 duration-500 hover:rotate-3 bg-gradient-to-r from-[#4D9DE0]/70 via-[#FF6B9C]/70 to-[#95F985]/70 bg-clip-text text-transparent text-6xl font-serif font-bold tracking-wider">
                  FLOM
                </span>
                <Smile
                  size={28}
                  className="relative top-1"
                  color="#FFE5A3"
                  fill="none"
                  strokeWidth={2}
                />
              </div>
              <span className="text-lg italic font-light tracking-wide bg-gradient-to-r from-[#4D9DE0]/70 via-[#FF6B9C]/70 to-[#95F985]/70 bg-clip-text text-transparent">
                Sieraden door Floor & Mose
              </span>
            </div>
          </div>
          <svg width="0" height="0" className="absolute">
            <defs>
              <linearGradient id="rainbow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: "#4D9DE0", stopOpacity: "0.7" }} />
                <stop offset="50%" style={{ stopColor: "#FF6B9C", stopOpacity: "0.7" }} />
                <stop offset="100%" style={{ stopColor: "#95F985", stopOpacity: "0.7" }} />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      <div className="mb-8">
        <ToggleGroup 
          type="multiple" 
          value={selectedCategories}
          onValueChange={handleCategoryChange}
          className="flex flex-wrap justify-center gap-2"
        >
          {categories.map((category) => (
            <ToggleGroupItem
              key={category}
              value={category}
              aria-label={`Filter by ${category}`}
              className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-[#0EA5E9] hover:bg-jewelry-hover data-[state=on]:bg-jewelry-accent data-[state=on]:text-[#0EA5E9] transition-all duration-200 font-medium"
            >
              {category}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex items-center justify-between border-b border-jewelry-accent pb-4">
        <h2 className="text-2xl font-light text-jewelry-text">Shop the look</h2>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {filteredProducts.map((product) => (
          <ProductCard key={product.id} {...product} />
        ))}
      </div>
    </div>
  );
};

export default ProductGrid;
